//
// Copyright (c) 2011-2021 by Curalate, Inc.
//

import React from 'react';
import styled from 'styled-components';

import { SmallTooltip } from '@components/molecules/tooltip/Tooltip';

export const DEFAULT_COPYRIGHT_MESSAGE =
  'This media is unavailable due to a copyright flag';

export const INSTAGRAM_COPYRIGHT_MESSAGE =
  'This media is unavailable due to a copyright flag on Instagram';

interface IOwnProps {
  placeholderType: PlaceholderType;
  size: number;
  copyrightMessage?: string;
}

type PlaceholderType = 'condensed' | 'full';

type Props = IOwnProps;

/**
 * The CopyrightedImagePlaceholder molecule renders a square image
 * placeholder with an optional explanation tooltip.
 *
 * @param placeholderType The display type of the placeholder
 * @param size The width/height of the placeholder (rendered as a square)
 * @param copyrightMessage The message to show below the copyright symbol (large theme) and in the tooltip (small theme)
 */
const CopyrightedImagePlaceholder = ({
  placeholderType,
  size,
  copyrightMessage = DEFAULT_COPYRIGHT_MESSAGE,
}: Props) => {
  return (
    <SquareContainer placeholderType={placeholderType} size={size}>
      <CopyrightContentContainer>
        <SmallTooltip
          content={<TooltipContent>{copyrightMessage}</TooltipContent>}
          target={<CopyrightSymbol>&copy;</CopyrightSymbol>}
          placement='right'
          isDisabled={placeholderType === 'full'}
        />
        {placeholderType === 'full' && (
          <CopyrightMessage>{copyrightMessage}</CopyrightMessage>
        )}
      </CopyrightContentContainer>
    </SquareContainer>
  );
};

const SquareContainer = styled.div<{
  placeholderType: PlaceholderType;
  size: number;
}>(
  ({ theme, placeholderType, size }) => `
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: ${size}px;
  height: ${size}px;
  background-color: ${theme.colors.gray0};
  padding: ${
    placeholderType === 'full' ? theme.spacings[2] : theme.spacings[1]
  };
  `,
);

const CopyrightContentContainer = styled.div`
  width: 100%;
  text-align: center;
`;

const CopyrightSymbol = styled.div(
  ({ theme }) => `
  color: ${theme.colors.aliases.bodySecondary};
  font-size: ${theme.fontSizes[1]};
  font-weight: ${theme.fontWeights.bold};
  `,
);

const TooltipContent = styled.div`
  text-align: left;
`;

const CopyrightMessage = styled.div(
  ({ theme }) => `
  color: ${theme.colors.aliases.bodyPrimary};
  `,
);

export default CopyrightedImagePlaceholder;
